import {useState, useEffect, Dispatch, SetStateAction} from "react";
import {parseJSON, differenceInMilliseconds, addMilliseconds} from "date-fns"

const dateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/;

function reviver(key: any, value: string) {
    if (dateFormat.test(value)) {
        return new Date(value);
    }

    return value;
}

const getStorageValue = (storage: Storage, key: string, defaultValue: any, expirationInMilliseconds?: number) => {
    if (typeof window !== "undefined") {
        const saved = storage.getItem(key);

        const initial = saved && JSON.parse(saved, reviver);
        if (initial) {
            const now = new Date();
            const expiration = addMilliseconds(initial?.creationDate, expirationInMilliseconds || 0);
            if (expirationInMilliseconds && expirationInMilliseconds > 0 && differenceInMilliseconds(now, expiration) > 0)
                return {value: defaultValue, isExpired: false};
        }

        return {value: initial?.value || defaultValue, isExpired: !initial?.value || initial?.value && differenceInMilliseconds(new Date(), initial?.creationDate) >= (expirationInMilliseconds || 0)};
    }
    return null;
};

interface IUseStorageArgs<T> {
    storageType?: 'localStorage' | 'sessionStorage'
    key: string,
    defaultValue?: T,
    expirationInMilliseconds?: number
}

export const useStorage = <T, >({key, defaultValue, expirationInMilliseconds, storageType}: IUseStorageArgs<T>): [T, Dispatch<SetStateAction<T>>, boolean, Dispatch<SetStateAction<boolean>>] => {
    const storage = storageType === "localStorage" ? localStorage : sessionStorage;
    const [value, setValue] = useState<T>(() => getStorageValue(storage, key, defaultValue, expirationInMilliseconds)?.value);
    const [isExpired, setIsExpired] = useState<boolean>(() => {
        const v = getStorageValue(storage, key, defaultValue, expirationInMilliseconds);
        return !v?.value || v?.value && v?.isExpired
    });

    useEffect(() => {
        const item = storage.getItem(key);
        if (item) {
            const parsedItem = JSON.parse(item, reviver);
            const now = new Date();
            const expiration = addMilliseconds(parsedItem.creationDate, expirationInMilliseconds || 0);
            const elapsed = differenceInMilliseconds(expiration, now);
            if ((expirationInMilliseconds || 0) > 0 && elapsed <= 0) {
                if (parsedItem instanceof Array) {
                    setValue(defaultValue || [] as T)
                }
                setIsExpired(true);
                return;
            }
        }
    }, [key, expirationInMilliseconds, storage, defaultValue]);

    useEffect(() => {
        if (isExpired) {
            storage.removeItem(key);
        }
    }, [isExpired]);

    useEffect(() => {
        storage.setItem(key, JSON.stringify({value, creationDate: new Date()}));
        setIsExpired(false);
    }, [storage, key, value]);

    return [value, setValue, isExpired, setIsExpired];
};